import { useMemo, useState, useEffect } from 'react'
import { toast } from 'sonner'
import { Repeat, CalendarClock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { useRecurringRules, useIncludeRecurring, type RecurringOccurrence } from '@/hooks/useRecurring'
import { useTransactions } from '@/hooks/useTransactions'
import { formatCurrency } from '@/lib/currency'
import { accountIcon } from '@/lib/accountIcon'
import { monthOccurrenceStatus } from '@/lib/recurring'
import { useT } from '@/i18n/I18nProvider'

function monthBounds(year: number, month0: number) {
  const iso = (d: Date) => d.toISOString().slice(0, 10)
  return { from: iso(new Date(year, month0, 1)), to: iso(new Date(year, month0 + 1, 0)) }
}
function fmtBr(isoDate: string) {
  const [y, m, d] = isoDate.split('-')
  return `${d}/${m}/${y}`
}

// Diálogo "Incluir recorrente": lista as recorrentes que caem no mês e ainda
// NÃO foram incluídas. Se accountId for passado, só as daquela conta/cartão.
export function IncludeRecurringDialog({
  open,
  onOpenChange,
  year,
  month0,
  accountId,
  title,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  year: number
  month0: number
  accountId?: string
  title?: string
}) {
  const { t } = useT()
  const { data: rules } = useRecurringRules()
  const range = useMemo(() => monthBounds(year, month0), [year, month0])
  const { data: monthTxs } = useTransactions(range)
  const include = useIncludeRecurring()

  const available = useMemo(() => {
    const items: (RecurringOccurrence & { accountName: string; key: string })[] = []
    for (const r of rules ?? []) {
      if (accountId && r.template.account_id !== accountId) continue
      // Casa por MÊS (conta + descrição/recurring_id), pareando 1:1 — cobre as
      // ocorrências importadas do LM (recurring_id nulo) lançadas em outro dia.
      for (const o of monthOccurrenceStatus(r, monthTxs ?? [], year, month0)) {
        if (!o.included)
          items.push({
            ruleId: r.id,
            date: o.date,
            template: r.template,
            accountName: r.account ? `${accountIcon(r.account)} ${r.account.name}` : '—',
            key: r.id + '|' + o.date,
          })
      }
    }
    return items.sort((a, b) => a.date.localeCompare(b.date))
  }, [rules, monthTxs, accountId, year, month0])

  const [sel, setSel] = useState<Set<string>>(new Set())
  useEffect(() => {
    if (open) setSel(new Set())
  }, [open])

  function toggle(key: string) {
    setSel((s) => {
      const n = new Set(s)
      n.has(key) ? n.delete(key) : n.add(key)
      return n
    })
  }
  const allSelected = available.length > 0 && sel.size === available.length

  async function add() {
    const chosen = available.filter((i) => sel.has(i.key))
    if (chosen.length === 0) return
    try {
      await include.mutateAsync(chosen.map(({ ruleId, date, template }) => ({ ruleId, date, template })))
      toast.success(t('rec.includeDialog.addedN', { count: chosen.length }))
      onOpenChange(false)
    } catch (e) {
      toast.error(t('rec.toast.includeError'), { description: (e as Error).message })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90svh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title ?? t('rec.includeDialog.title')}</DialogTitle>
          <DialogDescription>{t('rec.includeDialog.description')}</DialogDescription>
        </DialogHeader>

        <div className="py-2">
          {available.length === 0 ? (
            <p className="text-muted-foreground p-6 text-center text-sm">
              {t('rec.includeDialog.empty')}
            </p>
          ) : (
            <>
              <label className="text-muted-foreground mb-2 flex items-center gap-2 text-xs">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={(v) =>
                    setSel(v === true ? new Set(available.map((i) => i.key)) : new Set())
                  }
                />
                {t('rec.includeDialog.selectAll', { count: available.length })}
              </label>
              <div className="divide-border divide-y rounded-lg border">
                {available.map((it) => {
                  const isExpense = it.template.kind === 'expense'
                  return (
                    <label key={it.key} className="hover:bg-accent/40 flex cursor-pointer items-center gap-3 p-3">
                      <Checkbox checked={sel.has(it.key)} onCheckedChange={() => toggle(it.key)} />
                      <Repeat className="text-primary size-4 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">
                          {it.template.description || t('rec.noDescription')}
                        </div>
                        <div className="text-muted-foreground flex items-center gap-1 text-xs">
                          <CalendarClock className="size-3" /> {fmtBr(it.date)} · {it.accountName}
                        </div>
                      </div>
                      <div className={`tabular-nums text-sm font-medium ${isExpense ? '' : 'text-success'}`}>
                        {isExpense ? '-' : '+'}
                        {formatCurrency(it.template.amount, it.template.currency)}
                      </div>
                    </label>
                  )
                })}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('rec.includeDialog.close')}
          </Button>
          <Button onClick={add} disabled={sel.size === 0 || include.isPending}>
            {include.isPending
              ? t('rec.includeDialog.including')
              : sel.size > 0
                ? t('rec.includeDialog.includeCount', { count: sel.size })
                : t('rec.include')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
